import classNames from 'classnames';
import React, { useState } from 'react';
import type { PropsWithChildren } from 'react';
import Avatar from './Avatar';
import * as styles from './AvatarStack.module.scss';
import Button from './Button';
import Tooltip from './Tooltip';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const AVATAR_STACK_USERS_FRAGMENT = gql(`
  fragment AvatarStack_users on User {
    id
    ...Avatar_user
  }
`);

type UsersFragment = DocumentType<typeof AVATAR_STACK_USERS_FRAGMENT>;
type AvatarStackSpacing = 'tight' | 'loose';
type AvatarOverflowVariant = 'pill' | 'smallPill' | 'circle';

interface AvatarStackProps<T> {
  items: T[];
  size?: number;
  spacing?: AvatarStackSpacing;
  noWrap?: boolean;
  borderSize?: number;
  limit?: number;
  expandable?: boolean;
  countOverflowVariant?: AvatarOverflowVariant;
  countOverflowTooltip?: string;
}

type AvatarStackChildren<T> = T extends UsersFragment
  ? { children?: AvatarRenderFunction<UsersFragment> } // Make children optional for user avatars
  : { children: AvatarRenderFunction<T> };

type AvatarRenderFunction<T> = (
  item: T,
  options: { size: number; index: number },
) => React.ReactNode;

const defaultRenderAvatar = (
  item: UsersFragment,
  options: { size: number; index: number },
) => <Avatar key={item.id} user={item} size={options.size} />;

const AvatarStack = <T extends { id: string }>({
  items,
  size = 26,
  spacing = 'loose',
  borderSize,

  limit = Number.POSITIVE_INFINITY,
  countOverflowVariant = 'pill',
  expandable,
  countOverflowTooltip = '',
  noWrap = false,

  children = defaultRenderAvatar,
}: AvatarStackProps<T> & AvatarStackChildren<T>) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const shouldSlice = limit < items.length && !isExpanded;
  const visibleItems = shouldSlice ? items.slice(0, limit - 1) : items;
  const restCount = items.length - visibleItems.length;

  if (items.length === 0) {
    return null;
  }

  const renderItem = children as AvatarRenderFunction<T>;

  return (
    <AvatarStackContainer
      noWrap={noWrap}
      spacing={spacing}
      borderSize={borderSize}
    >
      {visibleItems.map((item, index) => (
        <AvatarStackItem key={item.id}>
          {renderItem(item, { size, index })}
        </AvatarStackItem>
      ))}
      {shouldSlice && (
        <AvatarStackOverflowPill
          totalCount={items.length}
          restCount={restCount}
          expandable={expandable}
          expand={() => setIsExpanded(true)}
          variant={countOverflowVariant}
          itemSize={size}
          tooltip={countOverflowTooltip}
        />
      )}
    </AvatarStackContainer>
  );
};

interface AvatarStackItemProps {
  className?: string;
  allowOverflow?: boolean;
  children: React.ReactNode;
}

export const AvatarStackItem = ({
  children,
  className,
  allowOverflow,
}: AvatarStackItemProps) => {
  return (
    <div
      className={classNames(styles.item, className, {
        [styles.allowItemOverflow]: allowOverflow,
      })}
    >
      {children}
    </div>
  );
};

export const AvatarStackContainer = ({
  children,
  noWrap,
  spacing,
  borderSize = 3,
}: PropsWithChildren<{
  noWrap?: boolean;
  spacing?: AvatarStackSpacing;
  borderSize?: number;
}>) => {
  return (
    <div
      className={classNames(styles.container, {
        [styles.noWrap]: noWrap,
        [styles.tight]: spacing === 'tight',
      })}
      style={
        { '--avatar-border-size': `${borderSize}px` } as React.CSSProperties
      }
    >
      {children}
    </div>
  );
};

interface AvatarStackOverflowPillProps {
  totalCount: number;
  restCount: number;
  expandable?: boolean;
  expand: () => void;
  variant: AvatarOverflowVariant;
  tooltip?: string;
  itemSize: number;
}

const AvatarStackOverflowPill = ({
  totalCount,
  restCount,
  expandable,
  variant,
  expand,
  itemSize,
  tooltip,
}: AvatarStackOverflowPillProps) => {
  const restCountPill = (
    <div
      className={classNames(styles.countContent, styles[variant])}
      style={
        variant === 'circle'
          ? { width: itemSize }
          : { fontSize: itemSize / 1.7 }
      }
    >
      {variant === 'circle' ? (
        totalCount
      ) : (
        <>
          <span className={styles.plus}>+</span>
          {restCount}
        </>
      )}
    </div>
  );

  return (
    <Tooltip content={tooltip} asChild>
      <div className={classNames(styles.item, styles.countWrapper)}>
        {expandable ? (
          <Button
            className={classNames(styles.buttonCount, styles.count)}
            onClick={expand}
          >
            {restCountPill}
          </Button>
        ) : (
          <div className={styles.count} style={{ height: itemSize }}>
            {restCountPill}
          </div>
        )}
      </div>
    </Tooltip>
  );
};

export default AvatarStack;
